import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import { IconClose } from './Icons';
import CoperturaAgenteAnim from './CoperturaAgenteAnim';
import './phases.css';

const nf = (n) => (n || 0).toLocaleString('it-IT');

// bucket esito -> etichetta/colore/barre per il flash "Risultato" dell'animazione
const ESITO_MAP = {
  coperto: { label: 'COPERTO', color: '#22c55e', bars: 4 },
  non_coperto: { label: 'NON COPERTO', color: '#94a3b8', bars: 1 },
  non_censito: { label: 'NON CENSITO', color: '#eab308', bars: 2 },
  errore: { label: 'ERRORE', color: '#ef4444', bars: 0 },
};

/* Com'è fatto il lotto: quanti record mai verificati e quante scadenze del timing, cioè
   la quota al lavoro. Le due nature non vanno confuse: i nuovi sono scoperta, le scadenze
   sono manutenzione (è da lì che esce il passaggio a FTTH). Torna null sui job che non
   hanno i conteggi — il bulk lanciato a mano, e i job salvati prima della quota. */
export function composizioneLotto(job) {
  if (!job || job.lotto_nuovi == null) return null;
  const voci = [];
  if (job.lotto_nuovi) voci.push(`${nf(job.lotto_nuovi)} nuovi`);
  if (job.lotto_riverifiche) voci.push(`${nf(job.lotto_riverifiche)} scadenze`);
  if (job.lotto_preferenziali) voci.push(`${nf(job.lotto_preferenziali)} FTTH vicina`);
  return voci.join(' · ');
}

/* Dialog dell'agente Copertura: riepilogo, esiti, velocità e proiezione 24h.
   Gemello di StatsDialog (456) ma con il radar e i dati copertura. */
export default function CoperturaStatsDialog({ open, onClose }) {
  const [stato, setStato] = useState(null);
  const [closing, setClosing] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [ultimoEsito, setUltimoEsito] = useState(null);
  const [flash, setFlash] = useState(0);   // scatta ad ogni record verificato (sincronizza il badge)
  const prevCountRef = useRef(null);

  // ESITO REALE: quale bucket (coperto/non_coperto/non_censito/errore) e' cresciuto -> flash sincronizzato
  useEffect(() => {
    const j = stato?.job;
    if (!j) return;
    const cur = { coperto: j.coperto || 0, non_coperto: j.non_coperto || 0, non_censito: j.non_censito || 0, errore: j.errore || 0 };
    const prev = prevCountRef.current;
    if (prev) {
      const grown = ['coperto', 'non_coperto', 'non_censito', 'errore'].find((k) => cur[k] > prev[k]);
      if (grown) { setUltimoEsito(ESITO_MAP[grown]); setFlash((f) => f + 1); }
    }
    prevCountRef.current = cur;
  }, [stato]);

  const handleClose = () => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => { setClosing(false); onClose?.(); }, 320);
  };

  useEffect(() => {
    if (!open) return;
    setClosing(false);
    setConfirmStop(false);
    const load = () => axios.get(`${API}/copertura/stato`).then((r) => setStato(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 1500);
    return () => clearInterval(id);
  }, [open]);

  const fermaAgente = async () => {
    setStopping(true);
    try {
      await axios.post(`${API}/copertura/agente`, { attivo: false });
      toast.info('AI copertura fermato');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    setStopping(false);
    setConfirmStop(false);
  };

  if (!open) return null;

  const job = stato?.job;
  const running = job && job.status === 'running';
  const standby = job && job.status === 'standby';
  const vel = job?.velocita_min || 0;
  const per24 = vel * 1440;
  const tot = job ? (job.coperto + job.non_coperto + job.non_censito + job.errore) : 0;
  const pct = (c) => (tot ? Math.round((c / tot) * 100) : 0);
  const composizione = composizioneLotto(job);
  const eta = job?.eta_secondi;
  const etaTxt = eta == null ? null : eta < 90 ? 'meno di 2 min'
    : eta < 5400 ? `~${Math.round(eta / 60)} min` : `~${(eta / 3600).toFixed(1)} h`;

  return createPortal(
    <div className={`bulk-overlay${closing ? ' closing' : ''}`}>
      <div className="bulk-dialog">
        <div className="bulk-bg">
          <div className="bulk-grid" />
          {running && (
            <div className="bulk-radar">
              <span className="bulk-ring r0" /><span className="bulk-ring r1" />
              <span className="bulk-ring r2" /><span className="bulk-ring r3" />
            </div>
          )}
        </div>

        <div className="bulk-content">
          <div className="bulk-head">
            <span className="bulk-title">AI Copertura TIM</span>
            <button className="bulk-x" onClick={handleClose}><IconClose size={16} color="#fff" /></button>
          </div>

          {!job ? (
            <div className="bulk-empty">
              <div className="bulk-empty-ico">📡</div>
              <p>{stato?.agente_attivo ? 'AI accesa — nessuna verifica in corso' : 'AI copertura fermo'}</p>
              {stato?.pending != null && (
                <p style={{ fontSize: 12, opacity: .7 }}>Da verificare: <b>{nf(stato.pending)}</b></p>
              )}
            </div>
          ) : (
            <div className="ph-wrap">
              <CoperturaAgenteAnim attivo={running} standby={standby} esito={ultimoEsito} flash={flash} />
              <div className="ph-label">{standby ? 'Standby — pausa anti-blocco' : 'Scansione copertura'}</div>
              <div className="ph-sub">
                {job.current_indirizzo
                  ? <>verifico: <span className="ph-num">{job.current_indirizzo}</span></>
                  : 'preparazione…'}
                {job.current_number ? <> · n. {job.current_number}</> : null}
              </div>

              {/* avanzamento lotto corrente */}
              <div className="ph-stats">
                <div className="ph-stats-head">
                  <span>Lotto corrente {nf(job.completed)}/{nf(job.total)}</span>
                  {etaTxt && <span style={{ opacity: .8 }}>ETA {etaTxt}</span>}
                </div>
                <div className="ph-stack">
                  <span style={{ width: `${pct(job.coperto)}%`, background: '#22c55e' }} />
                  <span style={{ width: `${pct(job.non_coperto)}%`, background: '#94a3b8' }} />
                  <span style={{ width: `${pct(job.non_censito)}%`, background: '#eab308' }} />
                  <span style={{ width: `${pct(job.errore)}%`, background: '#ef4444' }} />
                </div>
                <div className="ph-legend">
                  <span><i style={{ background: '#22c55e' }} />Coperti {nf(job.coperto)}</span>
                  <span><i style={{ background: '#94a3b8' }} />No {nf(job.non_coperto)}</span>
                  <span><i style={{ background: '#eab308' }} />N/C {nf(job.non_censito)}</span>
                  <span><i style={{ background: '#ef4444' }} />Err {nf(job.errore)}</span>
                </div>
                {composizione && (
                  <div className="ph-speed" style={{ borderTop: 'none', paddingTop: 0 }}>
                    <span style={{ opacity: .8 }}>Lotto</span>
                    <span>{composizione}</span>
                  </div>
                )}
                <div className="ph-speed">
                  <span>Velocità <b>{vel}</b>/min</span>
                  <span>Stima 24h <b>~{nf(per24)}</b></span>
                </div>
                {stato?.pending != null && (
                  <div className="ph-speed" style={{ borderTop: 'none', paddingTop: 0 }}>
                    <span>Da verificare <b>{nf(stato.pending)}</b></span>
                    {per24 > 0 && stato.pending > 0 && (
                      <span>Completamento <b>~{Math.max(1, Math.ceil(stato.pending / per24))} gg</b></span>
                    )}
                  </div>
                )}
              </div>

              {(running || standby) && (
                confirmStop ? (
                  <div className="bulk-confirm">
                    <span className="bulk-confirm-q">Fermare l'agente copertura?</span>
                    <div className="bulk-confirm-btns">
                      <button className="bulk-cancel" onClick={fermaAgente} disabled={stopping}>{stopping ? '…' : 'Sì'}</button>
                      <button className="bulk-keep" onClick={() => setConfirmStop(false)} disabled={stopping}>No</button>
                    </div>
                  </div>
                ) : (
                  <button className="bulk-cancel" onClick={() => setConfirmStop(true)}>Ferma agente</button>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
